/**
 * CampCard — displays a single upcoming ID camp.
 *
 * Mobile: single-column stacked layout.
 * Desktop: two-column internal flex (name+CTA left, metadata right).
 *
 * Design: WI-48 design artifact, Screen 2 / Screen 3 / Screen 4 detail.
 */
import React from 'react';
import { CalendarIcon, MapPinIcon } from '@heroicons/react/24/solid';
import type { CampDetail } from '../../api/school';
import { formatCampDateRange, formatPrice, normalizeAgeGroup } from '../../utils/dates';
import { scrollToCoaches } from '../../utils/scroll';
import { IconExternal } from './icons';

interface CampCardProps {
  camp: CampDetail;
  schoolName: string;
}

/** Build a location display string from nullable location fields. */
function buildLocationString(
  name: string | null,
  city: string | null,
  state: string | null
): string | null {
  const parts: string[] = [];
  if (name) parts.push(name);
  const cityState = [city, state].filter(Boolean).join(', ');
  if (cityState) parts.push(cityState);
  return parts.length > 0 ? parts.join(' · ') : null;
}

export function CampCard({ camp, schoolName }: CampCardProps) {
  // Primary guard: never call formatCampDateRange when date is unknown (WI-51b).
  const showDateTbd = camp.date_unknown;
  const dateRange = showDateTbd ? null : formatCampDateRange(camp.start_date, camp.end_date);

  const locationStr = buildLocationString(
    camp.location_name,
    camp.location_city,
    camp.location_state
  );
  const price = formatPrice(camp.price_cents);
  const normalizedAgeGroups = camp.age_groups.map(normalizeAgeGroup);
  const ageGroupsText = normalizedAgeGroups.length > 0
    ? normalizedAgeGroups.join(', ')
    : null;

  // Mobile: age+price on one combined line; desktop metadata column: separate rows
  const agePriceLine = [ageGroupsText, price].filter(Boolean).join(' · ');

  // Hub-link cards use amber border; dated cards use default gray border.
  const borderClass = showDateTbd ? 'border-amber-200' : 'border-gray-200';

  return (
    <div
      role="article"
      aria-label={camp.name}
      className={`rounded-lg border ${borderClass} shadow-sm p-4 bg-white`}
    >
      {/* Desktop: flex row — left (name+CTA) / right (metadata) */}
      {/* Mobile: single column stacked */}
      <div className="md:flex md:items-start md:gap-4">

        {/* Left column: name + CTA */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900 line-clamp-2">
            {camp.name}
            {camp.gender === 'coed' && (
              <span className="ml-1 text-xs text-gray-500 font-normal">· Co-ed</span>
            )}
          </p>

          {/* Mobile-only metadata rows (shown on mobile, hidden md+) */}
          <div className="md:hidden mt-1 space-y-0.5">
            {showDateTbd ? (
              <MetadataDateTbd />
            ) : (
              <MetadataDate dateRange={dateRange!} />
            )}
            {locationStr && <MetadataLocation location={locationStr} />}
            {agePriceLine && (
              <p className="text-xs text-gray-600">{agePriceLine}</p>
            )}
          </div>

          {/* CTA button */}
          <div className="mt-3">
            {camp.registration_url != null ? (
              <a
                href={camp.registration_url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={
                  showDateTbd
                    ? `View camps for ${camp.name} on school site`
                    : `Register for ${camp.name}`
                }
                className="inline-flex items-center justify-center w-full md:w-auto
                           min-h-[44px] px-4 py-2 rounded-lg
                           bg-brand-primary text-white text-sm font-semibold
                           hover:bg-brand-primary-dk
                           motion-safe:transition-colors
                           focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2"
              >
                {showDateTbd ? 'View camps on school site →' : 'Register on school site →'}
              </a>
            ) : (
              <button
                type="button"
                onClick={scrollToCoaches}
                aria-label={`Contact coaches for ${schoolName}`}
                className="inline-flex items-center justify-center w-full md:w-auto
                           min-h-[44px] px-4 py-2 rounded-lg
                           border border-gray-200 bg-white text-gray-700 text-sm font-medium
                           hover:bg-gray-50 hover:border-gray-300
                           motion-safe:transition-colors
                           focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2"
              >
                Contact coaches
              </button>
            )}
          </div>

          {/* Source link — de-emphasized footer link. WI-50 / WI-51b. */}
          {camp.source_url !== null && (
            <div className="mt-2">
              {showDateTbd ? (
                <a
                  href={camp.source_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`View camp hub for ${camp.name} (opens in new tab)`}
                  className="inline-flex items-center gap-1 text-xs text-gray-500
                             hover:text-gray-700 motion-safe:transition-colors
                             focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary rounded
                             min-h-[44px] py-2"
                >
                  Camp hub
                  <IconExternal size={12} />
                </a>
              ) : (
                <a
                  href={camp.source_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`View source listing for ${camp.name} (opens in new tab)`}
                  className="inline-flex items-center gap-1 text-xs text-gray-500
                             hover:text-gray-700 motion-safe:transition-colors
                             focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary rounded
                             min-h-[44px] py-2"
                >
                  View source listing
                  <IconExternal size={12} />
                </a>
              )}
            </div>
          )}
        </div>

        {/* Right column: metadata — desktop only */}
        <div className="hidden md:flex flex-col gap-0.5 text-right flex-shrink-0">
          {showDateTbd ? (
            <MetadataDateTbd />
          ) : (
            <MetadataDate dateRange={dateRange!} />
          )}
          {locationStr && <MetadataLocation location={locationStr} />}
          {agePriceLine && (
            <p className="text-xs text-gray-600">{agePriceLine}</p>
          )}
        </div>
      </div>
    </div>
  );
}

function MetadataDate({ dateRange }: { dateRange: string }) {
  return (
    <p className="flex items-center gap-1 text-xs text-gray-600 md:justify-end">
      <CalendarIcon className="w-3 h-3 flex-shrink-0 text-gray-400" aria-hidden="true" />
      {dateRange}
    </p>
  );
}

/**
 * Date slot for hub-link cards (date_unknown = true).
 * Uses amber-700 text (4.6:1 contrast on white — WCAG AA pass).
 * Calendar icon retained for structural position consistency.
 * Plain <p> (not <time>) to avoid leaking sentinel via datetime attribute. WI-51b.
 */
function MetadataDateTbd() {
  return (
    <p
      className="flex items-center gap-1 text-xs text-amber-700 md:justify-end"
      aria-label="Date to be announced"
    >
      <CalendarIcon className="w-3 h-3 flex-shrink-0 text-gray-400" aria-hidden="true" />
      Date to be announced
    </p>
  );
}

function MetadataLocation({ location }: { location: string }) {
  return (
    <p className="flex items-center gap-1 text-xs text-gray-600 md:justify-end">
      <MapPinIcon className="w-3 h-3 flex-shrink-0 text-gray-400" aria-hidden="true" />
      {location}
    </p>
  );
}
